import React, { useState, useContext } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faChartLine, 
  faSyncAlt,
  faCog,
  faUpload,
  faFileInvoice,
  faMoneyBillWave,
  faChartBar,
  faUsers,
  faArrowRight
} from "@fortawesome/free-solid-svg-icons";
import { RateLimitAlertContext } from "../contexts/RateLimitAlertContext";
import { AdminAccessAlertContext } from "../contexts/AdminAccessAlertContext";
import { BASE_URL } from "../config";
import axios from "axios";
import clsx from "clsx";

const DashboardHome_v2 = () => {
  const [loading, setLoading] = useState(false);
  const [hoveredCard, setHoveredCard] = useState(null);
  const { openAlert: openRateLimitAlert } = useContext(RateLimitAlertContext);
  const { openAlert: openAdminAccessAlert } = useContext(AdminAccessAlertContext);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("authentication/token");
      await axios.get(`${BASE_URL}/dashboard/refresh`, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (error) {
      if (error.response?.status === 401) window.location.href = "/login";
      if (error.response?.status === 403) openAdminAccessAlert();
      if (error.response?.status === 429) openRateLimitAlert();
    } finally {
      setLoading(false);
    }
  };

  const guideItems = [
    {
      title: "Manage Billing Fees",
      description: "Set up and view billing configurations for corporate accounts, including card replacement and admin fees.",
      link: "/corporate-settings",
      icon: faCog,
      color: "from-pink-500 to-rose-500",
      hoverColor: "from-pink-600 to-rose-600",
    },
    {
      title: "Upload Fees",
      description: "Upload card replacement or admin fee files to process billing data efficiently.",
      link: "/card-replacement-fee/upload-card-replacement-fee",
      icon: faUpload,
      color: "from-green-500 to-emerald-500",
      hoverColor: "from-green-600 to-emerald-600",
    },
    {
      title: "Generate Billing",
      description: "Create invoices for network access, admin, card printing, or interest fees.",
      link: "/billing/network-access-fee",
      icon: faFileInvoice,
      color: "from-purple-500 to-violet-500",
      hoverColor: "from-purple-600 to-violet-600",
    },
    {
      title: "Track Collections",
      description: "Monitor payment collections and view detailed transaction histories.",
      link: "/collections/dashboard",
      icon: faMoneyBillWave,
      color: "from-orange-500 to-amber-500",
      hoverColor: "from-orange-600 to-amber-600",
    },
    {
      title: "View Reports",
      description: "Access comprehensive reports to analyze billing and collection performance.",
      link: "/reports",
      icon: faChartBar,
      color: "from-red-500 to-rose-500",
      hoverColor: "from-red-600 to-rose-600",
    },
    {
      title: "User Management",
      description: "Administer users, roles, and permissions to secure and streamline operations.",
      link: "/users",
      icon: faUsers,
      color: "from-indigo-500 to-blue-500",
      hoverColor: "from-indigo-600 to-blue-600",
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-cyan-50/30 p-4 md:p-8">
      {/* Enhanced Background Effects */}
      <div className="pointer-events-none fixed inset-0 -z-10 opacity-60">
        <div className="absolute -top-40 -right-40 h-80 w-80 bg-gradient-to-br from-blue-200/40 to-cyan-200/40 rounded-full blur-3xl animate-pulse-slow" />
        <div className="absolute -bottom-40 -left-40 h-80 w-80 bg-gradient-to-br from-amber-200/40 to-orange-200/40 rounded-full blur-3xl animate-pulse-slow delay-1000" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-64 w-64 bg-gradient-to-br from-violet-200/30 to-purple-200/30 rounded-full blur-3xl animate-pulse-slow delay-500" />
      </div>

      <div className="max-w-7xl mx-auto space-y-8">
        {/* Enhanced Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white/90 backdrop-blur-md rounded-2xl shadow-lg border border-slate-200/60 transition-all hover:shadow-xl hover:scale-105">
              <FontAwesomeIcon
                icon={faChartLine}
                className="text-blue-600 text-xl"
              />
            </div>
            <div>
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="text-3xl font-bold bg-gradient-to-br from-slate-800 to-slate-600 bg-clip-text text-transparent tracking-tight">
                  Billing & Collection Portal
                </h1>
                <span className="inline-flex items-center gap-2 rounded-full bg-blue-50/80 text-blue-700 px-3 py-1 text-sm font-medium border border-blue-100/60 backdrop-blur-sm">
                  <span className="h-2 w-2 rounded-full bg-blue-500 animate-pulse" />
                  Financial Operations
                </span>
              </div>
              <p className="text-sm text-slate-500 mt-2 font-medium max-w-2xl">
                Manage corporate accounts, process fees, track collections, and generate comprehensive financial reports 
                {/* with enterprise-grade security. */}
              </p>
            </div>
          </div>
          
          {/* <button
            onClick={fetchData}
            disabled={loading}
            className={clsx(
              "flex items-center gap-3 px-5 py-3 rounded-xl text-sm font-semibold shadow-lg border transition-all duration-300 hover:scale-105 backdrop-blur-sm",
              loading
                ? "bg-slate-100/80 text-slate-500 border-slate-200/50 cursor-not-allowed"
                : "bg-white/90 text-slate-700 border-slate-200/60 hover:border-blue-300 hover:shadow-xl"
            )}
          >
            <FontAwesomeIcon
              icon={faSyncAlt}
              className={clsx(
                "text-sm transition-transform",
                loading ? "animate-spin text-slate-400" : "text-blue-600"
              )}
            />
            {loading ? "Refreshing..." : "Refresh Data"}
          </button> */}
        </div>

        {/* Enhanced Guide Section */}
        {/* <div className="bg-white/90 backdrop-blur-lg rounded-2xl shadow-xl p-8 border border-slate-200/50 transition-all hover:shadow-2xl"> */}
        <div >
          {/* <div className="flex items-center gap-4 mb-8">
            
            <div className="p-3 rounded-xl bg-blue-50/80 text-blue-700 border border-blue-100/60">
              <FontAwesomeIcon icon={faChartBar} className="text-lg" />
            </div>

            <div>
              <h2 className="text-2xl font-bold text-slate-800">
                Financial Operations Guide
              </h2>
              <p className="text-sm text-slate-500 mt-1">
                Streamline your billing and collection workflows with these integrated tools
              </p>
            </div>

          </div> */}

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {guideItems.map((item, index) => (
              <Link
                key={index}
                to={item.link}
                className={clsx(
                  "group relative overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 cursor-pointer backdrop-blur-sm",
                  "bg-gradient-to-br bg-[length:200%_200%] bg-left-top hover:bg-right-bottom border border-white/20",
                  hoveredCard === index ? item.hoverColor : item.color
                )}
                onMouseEnter={() => setHoveredCard(index)}
                onMouseLeave={() => setHoveredCard(null)}
              >
                {/* Animated Background Pattern */}
                <div className="absolute inset-0 opacity-10">
                  <div className="absolute -right-4 -top-4 w-20 h-20 bg-white/30 rounded-full blur-xl group-hover:scale-150 transition-transform duration-500" />
                  <div className="absolute -left-4 -bottom-4 w-16 h-16 bg-white/20 rounded-full blur-lg group-hover:scale-150 transition-transform duration-500 delay-100" />
                </div>

                {/* Main Content */}
                <div className="relative z-10 p-6 h-full flex flex-col">
                  <div className="flex items-start justify-between mb-4">
                    <div className="p-3 bg-white/20 rounded-xl backdrop-blur-sm">
                      <FontAwesomeIcon
                        icon={item.icon}
                        className="text-white text-lg"
                      />
                    </div>
                    <div className={clsx(
                      "w-8 h-8 bg-white/20 rounded-full flex items-center justify-center transition-all duration-300",
                      "opacity-0 group-hover:opacity-100 transform translate-x-2 group-hover:translate-x-0"
                    )}>
                      <FontAwesomeIcon
                        icon={faArrowRight}
                        className="text-white text-xs"
                      />
                    </div>
                  </div>

                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-white mb-3 leading-tight">
                      {item.title}
                    </h3>
                    <p className="text-white/80 text-sm leading-relaxed">
                      {item.description}
                    </p>
                  </div>

                  {/* Hover Indicator */}
                  <div className={clsx(
                    "mt-4 pt-4 border-t border-white/20 transition-all duration-300",
                    "opacity-0 group-hover:opacity-100"
                  )}>
                    <div className="flex items-center justify-between text-white/70 text-xs font-medium">
                      <span>Access Tool</span>
                      <span className="flex items-center gap-1">
                        Click to open
                        <FontAwesomeIcon icon={faArrowRight} className="text-xs" />
                      </span>
                    </div>
                  </div>
                </div>

                {/* Shine Effect on Hover */}
                <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                  <div className="absolute -inset-1 bg-gradient-to-r from-transparent via-white/10 to-transparent transform skew-x-12 group-hover:translate-x-[100%] transition-transform duration-1000" />
                </div>
              </Link>
            ))}
          </div>

          {/* Footer Note */}
          {/* <div className="mt-8 pt-6 border-t border-slate-200/50">
            <div className="flex items-center justify-center gap-2 text-slate-500 text-sm">
              <FontAwesomeIcon icon={faChartLine} className="text-slate-400" />
              <span>All financial operations are secured with enterprise-grade encryption</span>
            </div>
          </div> */}

        </div>

        {/* Quick Stats Section (Optional - you can add metrics later) */}
        {/* <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl p-6 text-white backdrop-blur-sm border border-white/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm font-medium">Active Accounts</p>
                <p className="text-2xl font-bold mt-1">--</p>
              </div>
              <div className="p-2 bg-white/20 rounded-lg">
                <FontAwesomeIcon icon={faUsers} className="text-lg" />
              </div>
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-emerald-500 to-teal-500 rounded-2xl p-6 text-white backdrop-blur-sm border border-white/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-emerald-100 text-sm font-medium">Pending Invoices</p>
                <p className="text-2xl font-bold mt-1">--</p>
              </div>
              <div className="p-2 bg-white/20 rounded-lg">
                <FontAwesomeIcon icon={faFileInvoice} className="text-lg" />
              </div>
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-purple-500 to-violet-500 rounded-2xl p-6 text-white backdrop-blur-sm border border-white/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-sm font-medium">Collections Today</p>
                <p className="text-2xl font-bold mt-1">--</p>
              </div>
              <div className="p-2 bg-white/20 rounded-lg">
                <FontAwesomeIcon icon={faMoneyBillWave} className="text-lg" />
              </div>
            </div>
          </div>
        </div> */}
      </div>
    </div>
  );
};

export default DashboardHome_v2;